/**
 * MatrixGuide: the visual guide of a subject's matrix (the Matrix tab of a subject folder: FolderMatrix), opened by
 * the "?" after the matrix's title, or by "How it works" on its empty page (both id "matrix").
 * - The steps follow the reader's part in this subject: who edits the matrix (`canEdit`: the subject's moderator and
 *   admins) gets how to plan it (a grade to start with, or the edit mode; the BTS week template; a row of a quarter;
 *   its A, B and C; the BTS standard; saving, and a save conflict), 5 to 8 steps; who only reads it gets how to read
 *   it (the grade tabs, the grade's summary, the weeks, what the bank lacks), on an empty matrix as what the page will
 *   show. Both end with the reading view's Tasks column (what tasks gave out for each row; for the subject's
 *   moderator and admins, `role`, its «+ Тапсырма беру»), a planner's only once the matrix has rows. The texts live
 *   under help.matrix.* and name the buttons through the buttons' own i18n keys, so the guide says what the screen
 *   says.
 * - It follows the screen: under 768px (FolderMatrix's breakpoint for the table or the cards, the header's framed
 *   "⋯" and the phones' Cancel / Save bar) the phone layout is drawn and said; the empty page's ways in come in its
 *   order (a ready matrix to copy first where the folder has no template).
 * Props, from FolderMatrix: `canEdit`; `role` (the reader's role in the subject: an author is told what to write, a
 * moderator or an admin how to give a task);
 * `empty` (the matrix has no rows yet: its empty page shows); `template` (the BTS week template of a «БТС-1»…«БТС-4»
 * folder, {round, weeks}, else null); `standard` ({A, B, C} of the BTS standard, null outside the «БТС» folders);
 * `points` (each level's points); `copyable` (another folder of the subject has a matrix to copy: the empty page's
 * copy card, else an item of the header's "⋯").
 */
import React from "react";
import { useTranslation } from "react-i18next";
import {
  CheckBadgeIcon,
  DocumentDuplicateIcon,
  EllipsisHorizontalIcon,
  EyeIcon,
  KeyIcon,
  PencilIcon,
  PencilSquareIcon,
  PlusIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
// The matrix's own ✓ (its grade tabs, the bank's «enough», the plan bar) is the solid one.
import { CheckIcon } from "@heroicons/react/20/solid";
import { HelpFlow, HelpIntro, HelpNote, HelpRoles, HelpStep, HelpSteps, HelpText, HelpTips } from "../HelpKit";
import {
  Mark,
  MiniBadge,
  MiniButton,
  MiniCard,
  MiniCheck,
  MiniDivider,
  MiniField,
  MiniLevel,
  MiniRadio,
  MiniRow,
  MiniScreen,
  MiniStack,
  MiniStepper,
  MiniTable,
  MiniText,
} from "../Mini";
import { useMediaQuery } from "../../../pages/Exams/TestBuilder/ui";
import { DIFFICULTY_LEVELS, DIFFICULTY_POINTS } from "../../Difficulty/Difficulty";
import { countOf, gradeLabel, levelsText, plannedOf, pointsOf, quarterLabel, toNumber } from "../../QuestionFolders/matrix/matrixModel";
import { canAssignIn, canSeeTasksIn } from "../../../utils/folders";

// The example grades: the one being planned, and another tab.
const GRADE = 10;
const OTHER_GRADE = 11;
// A plan that does not meet the standard yet (the standard less two A and one C), for the plan bar.
const SHORT_BY = { A: 2, B: 0, C: 1 };

// One text a line: Mini texts are inline, so two of them in a stack would run on in one line.
function Line({ children }) {
  return <MiniRow>{children}</MiniRow>;
}

// Two parts Mini lacks so far, drawn here with Mini's own sizes and colours (to move into Mini.js):
// - GradeTab: a grade tab as ClassTabs draws it, its ✓ (`check` "ok": the plan meets the standard) or amber dot
//   ("off": it differs) after the label (MiniChip puts a dot or an icon before it).
// - NoTopic: a week without a topic as its row says it (RowName): an amber dot, then amber text (MiniBadge is a pill).
function GradeTab({ selected = false, check = null, mark = false, children }) {
  const tab = (
    <span
      className={`inline-flex h-6 min-w-0 max-w-full items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-[11px] ring-1 ring-inset md:h-7 md:text-xs ${
        selected ? "bg-blue-50 font-semibold text-blue-700 ring-blue-600" : "bg-white font-medium text-slate-700 ring-slate-200"
      }`}
    >
      <span className="truncate">{children}</span>
      {check === "ok" && <CheckIcon className="h-3 w-3 shrink-0 text-emerald-600 md:h-3.5 md:w-3.5" />}
      {check === "off" && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />}
    </span>
  );
  return mark ? (
    <Mark pointer round="full">
      {tab}
    </Mark>
  ) : (
    tab
  );
}

function NoTopic({ children }) {
  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1.5 whitespace-nowrap text-[11px] font-medium text-amber-800 md:text-xs">
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
      <span className="truncate">{children}</span>
    </span>
  );
}

export default function MatrixGuide({ canEdit = false, role = "", empty = false, template = null, standard = null, points, copyable = false }) {
  const [t] = useTranslation();
  // FolderMatrix's breakpoint: the table or the cards, the header's "⋯" and the phones' Cancel / Save bar.
  const wide = useMediaQuery("(min-width: 768px)");
  const ex = (key) => t(`help.common.example.${key}`);
  const own = (key) => t(`help.matrix.example.${key}`);
  const pts = { ...DIFFICULTY_POINTS, ...(points || {}) };

  const grade = gradeLabel(t, GRADE);
  const tasks = (count) => t("folders.matrix.tasks", { count });
  const plan = (counts) => `${tasks(countOf(counts))} · ${t("difficulty.points", { count: pointsOf(counts, pts) })}`;
  const week = (n) => t("folders.matrix.week_n", { n });
  const whole = t("folders.matrix.whole_topic");
  const subjectPath = `${ex("exam")} · ${ex("subject")}`;
  const roleLabels = { previous: t("folders.matrix.role_previous"), current: t("folders.matrix.role_current") };
  // As MatrixEmpty decides: the template leads the way where it has weeks; elsewhere a ready matrix to copy comes
  // first (recommended), before starting a grade from scratch.
  const templateWeeks = template?.weeks || [];
  const hasTemplate = templateWeeks.length > 0;
  const copyFirst = empty && copyable && !hasTemplate;
  // The labels of the real controls.
  const label = {
    edit: t("folders.matrix.edit"),
    save: t("buttons.save"),
    cancel: t("buttons.cancel"),
    begin: t("buttons.begin"),
    gradeField: t("folders.matrix.empty_new_grade"),
    addGrade: t("folders.matrix.add_class"),
    // `context`: a language may word a quarter apart (as FolderMatrix asks for it).
    addRow: t("folders.matrix.add_to_quarter", { quarter: "I", context: "1" }),
    submit: t("folders.matrix.form_add_row"),
    change: t("folders.matrix.edit_topic"),
    choose: t("folders.matrix.choose_topic"),
    fill: t("folders.matrix.template_fill"),
    start: hasTemplate ? t("folders.matrix.empty_template_start", { round: template.round }) : "",
    blank: t("folders.matrix.empty_start_blank"),
    copyEmpty: t("folders.matrix.empty_copy_button"),
    copy: t("folders.matrix.copy_button"),
    keep: t("folders.matrix.conflict_keep"),
    discard: t("folders.matrix.conflict_discard"),
  };
  // "⋯" as the header draws it: plain from md up, framed on phones.
  const more = <MiniButton variant={wide ? "quiet" : "outline"} icon={EllipsisHorizontalIcon} />;

  // The example plan: the standard met in the grade being planned (or some counts outside the «БТС» folders), and
  // the same plan two A and one C short of it in the plan bar.
  const full = standard ? Object.fromEntries(DIFFICULTY_LEVELS.map((level) => [level, toNumber(standard[level])])) : { A: 6, B: 4, C: 2 };
  const short = Object.fromEntries(DIFFICULTY_LEVELS.map((level) => [level, Math.max(0, full[level] - SHORT_BY[level])]));
  const pointsNote = (
    <p>
      <HelpText i18nKey="help.matrix.notes.points" values={{ a: pts.A, b: pts.B, c: pts.C }} />
    </p>
  );
  // A week's topic over its subtopics, one a line (as RowName shows them), or the whole topic.
  const topicCell = (topic, subtopics, mark = false) => {
    const lines = subtopics ? subtopics.map((name) => `• ${name}`) : [whole];
    const list = (
      <MiniStack tight>
        {lines.map((line) => (
          <Line key={line}>
            <MiniText variant="muted">{line}</MiniText>
          </Line>
        ))}
      </MiniStack>
    );
    return (
      <MiniStack tight>
        <MiniText variant="strong" truncate>
          {topic}
        </MiniText>
        {mark ? <Mark round="md">{list}</Mark> : list}
      </MiniStack>
    );
  };
  const subtopics = [own("subtopic_1"), own("subtopic_2")];
  const levelColumns = (width, withPoints = false) =>
    DIFFICULTY_LEVELS.map((level) => ({ label: <MiniLevel level={level} points={withPoints} />, width, align: "center" }));

  // ---- Who reads the matrix ----

  const gradeStep = (
    <HelpStep
      key="grade"
      title={t("help.matrix.steps.grade.title")}
      illustration={
        <MiniScreen title={subjectPath}>
          <Line>
            <MiniText variant="strong">{t("folders.matrix.title")}</MiniText>
          </Line>
          <MiniRow className="gap-y-3">
            <GradeTab selected check={standard ? "ok" : null}>
              {`${grade} · ${tasks(countOf(full))}`}
            </GradeTab>
            <GradeTab check={standard ? "off" : null} mark>
              {`${gradeLabel(t, OTHER_GRADE)} · ${tasks(countOf(short))}`}
            </GradeTab>
          </MiniRow>
        </MiniScreen>
      }
    >
      <p>{t("help.matrix.steps.grade.text")}</p>
      {standard && (
        <p>
          <HelpText i18nKey="help.matrix.steps.grade.text_standard" />
        </p>
      )}
    </HelpStep>
  );

  const summaryStep = (
    <HelpStep
      key="summary"
      title={t("help.matrix.steps.summary.title")}
      illustration={
        <MiniStack>
          {/* The grade's summary cards (ClassSummary): a label, the value, a line under it. */}
          <MiniCard tone={standard ? "green" : "white"}>
            <MiniStack tight>
              <Line>
                <MiniText variant="muted">{standard ? t("folders.matrix.standard_ok") : t("folders.matrix.plan_label")}</MiniText>
              </Line>
              <Line>
                <MiniText variant="title">{plan(full)}</MiniText>
              </Line>
              <Line>
                <MiniText variant="muted">{levelsText(full)}</MiniText>
              </Line>
            </MiniStack>
          </MiniCard>
          <MiniCard mark={{ pointer: false }}>
            <MiniStack tight>
              <Line>
                <MiniText variant="muted">{`${t("folders.matrix.role_current_title")} · III`}</MiniText>
              </Line>
              <Line>
                <MiniText variant="title">{plan(short)}</MiniText>
              </Line>
            </MiniStack>
          </MiniCard>
          <MiniCard>
            <MiniStack tight>
              <Line>
                <MiniText variant="muted">{t("folders.matrix.bank_card_label")}</MiniText>
              </Line>
              <Line>
                <MiniText variant="title">{t("folders.matrix.questions", { count: 56 })}</MiniText>
              </Line>
              <Line>
                <MiniText variant="muted">{t("folders.matrix.bank_card_ok")}</MiniText>
              </Line>
            </MiniStack>
          </MiniCard>
        </MiniStack>
      }
    >
      <p>{t("help.matrix.steps.summary.text")}</p>
      <p>
        <HelpText i18nKey="help.matrix.steps.summary.text_roles" values={roleLabels} />
      </p>
    </HelpStep>
  );

  const firstRow = { A: 1, B: 1, C: 0 };
  const rowsStep = (
    <HelpStep
      key="rows"
      title={t("help.matrix.steps.rows.title")}
      illustration={
        wide ? (
          <MiniStack>
            <MiniRow>
              <MiniText variant="strong">{quarterLabel(t, 2)}</MiniText>
              <MiniBadge>{roleLabels.previous}</MiniBadge>
            </MiniRow>
            <MiniTable
              columns={[
                { label: t("folders.matrix.col_week"), width: "4.5rem" },
                t("folders.matrix.col_topic_sub"),
                ...levelColumns("2.25rem"),
                { label: t("folders.matrix.col_total"), width: "4.25rem", align: "right" },
              ]}
              rows={[
                [week(6), topicCell(ex("topic"), subtopics, true), "1", "1", "—", "2"],
                [week(7), topicCell(ex("topic_2"), null), "2", "1", "1", "4"],
              ]}
            />
          </MiniStack>
        ) : (
          <MiniStack>
            <MiniRow>
              <MiniText variant="strong">{quarterLabel(t, 2)}</MiniText>
              <MiniBadge>{roleLabels.previous}</MiniBadge>
            </MiniRow>
            <MiniCard>
              {topicCell(ex("topic"), subtopics, true)}
              <Line>
                <MiniText variant="muted">{`${week(6)} · ${t("folders.matrix.hours_n", { count: 2 })}`}</MiniText>
              </Line>
              <Line>
                <MiniText>{`${levelsText(firstRow)} — ${plan(firstRow)}`}</MiniText>
              </Line>
            </MiniCard>
          </MiniStack>
        )
      }
    >
      <p>
        <HelpText i18nKey="help.matrix.steps.rows.text" values={{ whole }} />
      </p>
      {pointsNote}
    </HelpStep>
  );

  const shortText = t("folders.matrix.bank_short", { levels: "B 1" });
  const okBadge = (
    <MiniBadge tone="green" icon={CheckIcon}>
      {t("folders.matrix.bank_ok")}
    </MiniBadge>
  );
  const shortBadge = (
    <MiniStack tight>
      <MiniRow>
        <MiniBadge tone="red" mark={{ pointer: false }}>
          {shortText}
        </MiniBadge>
      </MiniRow>
      <Line>
        <MiniText variant="muted">{t("folders.matrix.bank_unapproved", { count: 1 })}</MiniText>
      </Line>
    </MiniStack>
  );
  const shortRow = { A: 2, B: 1, C: 1 };
  const bankStep = (
    <HelpStep
      key="bank"
      title={t("help.matrix.steps.bank.title")}
      illustration={
        wide ? (
          <MiniTable
            columns={[t("folders.matrix.col_topic_sub"), ...levelColumns("2.25rem"), { label: t("folders.matrix.col_bank"), width: "42%" }]}
            rows={[
              [ex("topic"), "1", "1", "—", okBadge],
              [ex("topic_2"), "2", "1", "1", shortBadge],
            ]}
          />
        ) : (
          <MiniCard>
            <Line>
              <MiniText variant="strong">{ex("topic_2")}</MiniText>
            </Line>
            <Line>
              <MiniText>{`${levelsText(shortRow)} — ${plan(shortRow)}`}</MiniText>
            </Line>
            {shortBadge}
          </MiniCard>
        )
      }
    >
      <p>
        <HelpText
          i18nKey={wide ? "help.matrix.steps.bank.text" : "help.matrix.steps.bank.text_phone"}
          values={{ column: t("folders.matrix.col_bank"), ok: t("folders.matrix.bank_ok"), short: shortText, pending: t("folders.matrix.bank_unapproved", { count: 1 }) }}
        />
      </p>
      {role === "author" && <p>{t("help.matrix.steps.bank.text_author")}</p>}
    </HelpStep>
  );

  // ---- The tasks given out (TZ 2.2) ----

  // The reading view's Tasks column (a line at the foot of each week's card on phones): how many of a row's questions
  // tasks give out, and for the subject's moderator and admins «+ Тапсырма беру» on each row; the grade's totals line
  // counts them too.
  const assigns = canAssignIn(role);
  const tasksRow = { A: 2, B: 1, C: 1 };
  const otherRow = { A: 1, B: 1, C: 0 };
  const given = { A: 2, B: 1, C: 0 };
  const givenText = t("folders.matrix.assigned", {
    levels: DIFFICULTY_LEVELS.filter((level) => given[level] > 0)
      .map((level) => `${level} ${given[level]}`)
      .join(" · "),
  });
  const giveLabel = t("folders.matrix.give_task");
  const giveButton = (mark) => (
    <MiniRow>
      <MiniButton variant="link" icon={PlusIcon} mark={mark}>
        {giveLabel}
      </MiniButton>
    </MiniRow>
  );
  // The givers' marker is on their button; the others' on what was given.
  const givenLine = (
    <Line>
      {assigns ? (
        <MiniText>{givenText}</MiniText>
      ) : (
        <Mark round="md">
          <MiniText>{givenText}</MiniText>
        </Mark>
      )}
    </Line>
  );
  // Not tight: the marked button's ring needs room under the line above it.
  const givenCell = (
    <MiniStack tight={!assigns}>
      {givenLine}
      {assigns && giveButton(true)}
    </MiniStack>
  );
  const totals = Object.fromEntries(DIFFICULTY_LEVELS.map((level) => [level, tasksRow[level] + otherRow[level]]));
  const totalsLine = (
    <>
      <MiniDivider />
      <MiniRow>
        <MiniText variant="strong">{t("folders.matrix.totals_class", { grade })}</MiniText>
        <MiniText>{levelsText(totals)}</MiniText>
        <MiniText>{plan(totals)}</MiniText>
        <MiniText variant="muted">{t("folders.matrix.assigned_total", { count: countOf(given) })}</MiniText>
      </MiniRow>
    </>
  );
  const tasksStep = (
    <HelpStep
      key="tasks"
      title={t(assigns ? "help.matrix.steps.tasks.title_give" : "help.matrix.steps.tasks.title")}
      illustration={
        <MiniStack>
          {wide ? (
            <MiniTable
              columns={[t("folders.matrix.col_topic_sub"), ...levelColumns("2.25rem"), { label: t("folders.matrix.col_tasks"), width: "40%" }]}
              rows={[
                [ex("topic"), "2", "1", "1", givenCell],
                [ex("topic_2"), "1", "1", "—", assigns ? giveButton(false) : "—"],
              ]}
            />
          ) : (
            <MiniCard>
              <Line>
                <MiniText variant="strong">{ex("topic")}</MiniText>
              </Line>
              <Line>
                <MiniText>{`${levelsText(tasksRow)} — ${plan(tasksRow)}`}</MiniText>
              </Line>
              <MiniRow>{okBadge}</MiniRow>
              <MiniDivider />
              {givenCell}
            </MiniCard>
          )}
          {totalsLine}
        </MiniStack>
      }
    >
      <p>
        <HelpText
          i18nKey={wide ? "help.matrix.steps.tasks.text" : "help.matrix.steps.tasks.text_phone"}
          values={{ column: t("folders.matrix.col_tasks"), given: givenText, over: t("folders.matrix.assigned_over") }}
        />
      </p>
      {(assigns || canSeeTasksIn(role)) && (
        <p>
          {assigns && (
            <>
              <HelpText i18nKey="help.matrix.steps.tasks.text_give" values={{ give: giveLabel }} />{" "}
            </>
          )}
          <HelpText i18nKey="help.matrix.steps.tasks.text_tab" values={{ tab: t("folders.tab_tasks") }} />
        </p>
      )}
    </HelpStep>
  );

  // ---- Who plans the matrix ----

  // The template's first week, as its row waits for a topic (its «Тақырып таңдау» is the second thing to press).
  const firstWeek = templateWeeks[0] || null;
  const weekCounts = firstWeek ? Object.fromEntries(DIFFICULTY_LEVELS.map((level) => [level, toNumber(firstWeek.planned?.[level])])) : firstRow;
  const weekCard = (
    <MiniCard>
      <MiniRow className="justify-between gap-y-3">
        <MiniRow>
          <MiniText variant="muted">{firstWeek ? `${quarterLabel(t, firstWeek.quarter)} · ${week(firstWeek.week)}` : week(1)}</MiniText>
          <NoTopic>{t("folders.matrix.no_topic")}</NoTopic>
        </MiniRow>
        <MiniButton variant="link" mark="2">
          {label.choose}
        </MiniButton>
      </MiniRow>
      <Line>
        <MiniText>{`${levelsText(weekCounts)} — ${plan(weekCounts)}`}</MiniText>
      </Line>
    </MiniCard>
  );

  // The empty page's way in: a grade from the BTS template (whose weeks then wait for their topics: one step for
  // both), or a grade from scratch ("Or …" after a ready matrix to copy, where the page recommends that).
  const startStep = hasTemplate ? (
    <HelpStep
      key="start"
      title={t("help.matrix.steps.start.title_template")}
      illustration={
        <MiniStack>
          {/* The page's template card (framed blue there: its marked main button says the same here). */}
          <MiniCard>
            <MiniRow>
              <MiniText variant="strong">{t("folders.matrix.empty_template_title", { round: template.round })}</MiniText>
              <MiniBadge tone="blue">{t("folders.matrix.empty_copy_badge")}</MiniBadge>
            </MiniRow>
            <MiniRow className="gap-y-3">
              <MiniField label={label.gradeField} value={grade} select />
              <MiniButton variant="primary" icon={PlusIcon} mark="1">
                {label.start}
              </MiniButton>
              <MiniButton variant="link">{label.blank}</MiniButton>
            </MiniRow>
          </MiniCard>
          {weekCard}
        </MiniStack>
      }
    >
      <p>
        <HelpText i18nKey="help.matrix.steps.start.text_template" values={{ grade: label.gradeField, start: label.start, blank: label.blank }} />
      </p>
      <p>
        <HelpText i18nKey="help.matrix.steps.start.text_template_weeks" values={{ choose: label.choose }} />{" "}
        <HelpText i18nKey="help.matrix.steps.template.text_blocking" />
      </p>
    </HelpStep>
  ) : (
    <HelpStep
      key="start"
      title={t(copyFirst ? "help.matrix.steps.start.title_or" : "help.matrix.steps.start.title")}
      illustration={
        <MiniCard>
          <Line>
            <MiniText variant="strong">{t("folders.matrix.empty_new_title")}</MiniText>
          </Line>
          <MiniRow className="gap-y-3">
            <MiniField label={label.gradeField} value={grade} select />
            <MiniButton variant={copyable ? "outline" : "primary"} icon={PlusIcon} mark>
              {label.begin}
            </MiniButton>
          </MiniRow>
        </MiniCard>
      }
    >
      <HelpText i18nKey="help.matrix.steps.start.text" values={{ grade: label.gradeField, begin: label.begin, add: label.addGrade }} />
    </HelpStep>
  );

  // The empty page's copy card: first and recommended (blue) where the folder has no template.
  const copyStep = (
    <HelpStep
      key="copy"
      title={t(copyFirst ? "help.matrix.steps.copy.title_first" : "help.matrix.steps.copy.title")}
      illustration={
        <MiniCard>
          <MiniRow>
            <MiniText variant="strong">{t("folders.matrix.empty_copy_title")}</MiniText>
            {copyFirst && <MiniBadge tone="blue">{t("folders.matrix.empty_copy_badge")}</MiniBadge>}
          </MiniRow>
          <MiniField label={t("folders.matrix.copy_source")} value={`${ex("exam_2")} · ${ex("subject")} — ${t("folders.matrix.rows_n", { count: 8 })}`} select />
          <MiniRow>
            <MiniButton variant={copyFirst ? "primary" : "outline"} icon={DocumentDuplicateIcon} mark>
              {label.copyEmpty}
            </MiniButton>
          </MiniRow>
        </MiniCard>
      }
    >
      <HelpText i18nKey="help.matrix.steps.copy.text" values={{ copy: label.copyEmpty }} />
    </HelpStep>
  );

  const editStep = (
    <HelpStep
      key="edit"
      title={t("help.matrix.steps.edit.title")}
      illustration={
        <MiniScreen title={subjectPath}>
          {wide ? (
            <MiniRow className="justify-between gap-y-3">
              <MiniText variant="strong">{t("folders.matrix.title")}</MiniText>
              <MiniRow>
                <MiniButton variant="primary" icon={PencilSquareIcon} mark>
                  {label.edit}
                </MiniButton>
                {copyable && more}
              </MiniRow>
            </MiniRow>
          ) : (
            <>
              <Line>
                <MiniText variant="strong">{t("folders.matrix.title")}</MiniText>
              </Line>
              <MiniRow className="flex-nowrap gap-y-3">
                <MiniButton variant="primary" icon={PencilSquareIcon} mark>
                  {label.edit}
                </MiniButton>
                {copyable && more}
              </MiniRow>
            </>
          )}
          <MiniRow>
            <GradeTab selected check={standard ? "ok" : null}>{`${grade} · ${tasks(countOf(full))}`}</GradeTab>
          </MiniRow>
        </MiniScreen>
      }
    >
      <p>
        <HelpText i18nKey="help.matrix.steps.edit.text" values={{ edit: label.edit, save: label.save }} />
      </p>
      <p>
        <HelpText i18nKey="help.matrix.steps.edit.text_grade" values={{ add: label.addGrade }} />
      </p>
    </HelpStep>
  );

  // The edit mode's template card (while the grade lacks some of the template's weeks), then a week of it waiting for
  // its topic. An empty matrix tells the template in its start step.
  const templateStep = hasTemplate && !empty && (
    <HelpStep
      key="template"
      title={t("help.matrix.steps.template.title")}
      illustration={
        <MiniStack>
          <MiniCard tone="slate">
            <MiniRow className="justify-between gap-y-3">
              <MiniText variant="strong">
                {t("folders.matrix.template_title", {
                  round: template.round,
                  weeks: t("folders.matrix.weeks_n", { count: templateWeeks.length }),
                  levels: levelsText(plannedOf(templateWeeks)),
                })}
              </MiniText>
              <MiniButton variant="outline" mark="1">
                {label.fill}
              </MiniButton>
            </MiniRow>
          </MiniCard>
          {weekCard}
        </MiniStack>
      }
    >
      <p>
        <HelpText i18nKey="help.matrix.steps.template.text" values={{ round: template.round, button: label.fill, choose: label.choose }} />
      </p>
      <p>
        <HelpText i18nKey="help.matrix.steps.template.text_blocking" />
      </p>
    </HelpStep>
  );

  const addStep = (
    <HelpStep
      key="add"
      title={t("help.matrix.steps.add.title")}
      illustration={
        <MiniStack>
          <MiniRow>
            <MiniText variant="strong">{quarterLabel(t, 1)}</MiniText>
          </MiniRow>
          <MiniRow className="gap-y-3">
            <MiniButton variant="link" icon={PlusIcon} mark="1">
              {label.addRow}
            </MiniButton>
          </MiniRow>
          <MiniCard tone="blue">
            <Line>
              <MiniText variant="strong">{t("folders.matrix.form_title", { grade, quarter: quarterLabel(t, 1) })}</MiniText>
            </Line>
            <MiniRow className="gap-y-3">
              <MiniField label={t("folders.matrix.form_week")} value={week(3)} select />
              <MiniField label={t("folders.matrix.form_hours")} value="2" />
              <MiniField label={t("folders.matrix.form_topic", { grade })} value={ex("topic")} select className="flex-1" />
            </MiniRow>
            <Line>
              <MiniText variant="muted">{t("folders.matrix.form_part")}</MiniText>
            </Line>
            <MiniRow>
              <MiniRadio variant="plain" checked>
                {t("folders.matrix.form_by_subtopics")}
              </MiniRadio>
            </MiniRow>
            {/* Two of the topic's three subtopics: ticking all of them would turn the form to the whole topic. */}
            {[...subtopics, own("subtopic_3")].map((name, index) => (
              <MiniRow key={name}>
                <MiniCheck checked={index < subtopics.length}>{name}</MiniCheck>
              </MiniRow>
            ))}
            <MiniRow>
              <MiniRadio variant="plain">{whole}</MiniRadio>
            </MiniRow>
            <MiniRow className="justify-end gap-y-3">
              <MiniButton variant={wide ? "quiet" : "outline"}>{label.cancel}</MiniButton>
              <MiniButton variant="primary" mark="2">
                {label.submit}
              </MiniButton>
            </MiniRow>
          </MiniCard>
        </MiniStack>
      }
    >
      <p>
        <HelpText i18nKey="help.matrix.steps.add.text" values={{ add: label.addRow, whole, submit: label.submit }} />{" "}
        <HelpText i18nKey="help.matrix.steps.add.text_all" values={{ whole }} />
      </p>
      <p>
        <HelpText i18nKey="help.matrix.steps.add.text_change" values={{ change: label.change }} />
      </p>
    </HelpStep>
  );

  // A row's counts, each with the bank's approved questions of its level under it (the A one marked).
  const counts = { A: 2, B: 1, C: 1 };
  const bank = { A: 3, B: 3, C: 0 };
  const stepper = (level) => (
    <MiniStack tight>
      <MiniRow>
        <MiniStepper value={String(counts[level])} mark={level === "A"} />
      </MiniRow>
      <Line>
        <MiniText variant="muted">{t("folders.matrix.bank_hint", { count: bank[level] })}</MiniText>
      </Line>
    </MiniStack>
  );
  const countsStep = (
    <HelpStep
      key="counts"
      title={t("help.matrix.steps.counts.title")}
      illustration={
        wide ? (
          <MiniTable
            columns={[t("folders.matrix.col_topic_sub"), ...levelColumns("5rem", true)]}
            rows={[[topicCell(ex("topic"), subtopics), stepper("A"), stepper("B"), stepper("C")]]}
          />
        ) : (
          <MiniCard>
            {topicCell(ex("topic"), subtopics)}
            {DIFFICULTY_LEVELS.map((level) => (
              <MiniRow key={level} className="justify-between gap-y-3">
                <MiniLevel level={level} />
                {stepper(level)}
              </MiniRow>
            ))}
          </MiniCard>
        )
      }
    >
      <p>
        <HelpText i18nKey="help.matrix.steps.counts.text" values={{ bank: t("folders.matrix.bank_hint", { count: bank.A }) }} />
      </p>
      {!wide && <p>{t("help.matrix.steps.counts.text_phone")}</p>}
    </HelpStep>
  );

  // The plan bar: against the standard (what is left, by level), or the plan's sums outside the «БТС» folders.
  const left = DIFFICULTY_LEVELS.filter((level) => full[level] - short[level] > 0).map((level) =>
    t("folders.matrix.budget_left", { level, count: full[level] - short[level] })
  );
  const levelBadge = (level) => {
    const off = standard && short[level] !== full[level];
    return (
      <MiniBadge key={level} tone={off ? "amber" : "slate"} icon={standard && !off ? CheckIcon : undefined}>
        {standard ? `${level} ${short[level]} / ${full[level]}` : `${level} ${short[level]}`}
      </MiniBadge>
    );
  };
  const planBar = standard ? (
    <MiniCard tone="amber" mark={{ pointer: false }}>
      {wide ? (
        <MiniStack tight>
          <Line>
            <MiniText variant="strong">
              {t("folders.matrix.standard_line", { tasks: tasks(countOf(full)), points: t("difficulty.points", { count: pointsOf(full, pts) }) })}
            </MiniText>
          </Line>
          <Line>
            <MiniText>{`${t("folders.matrix.budget_prefix")} ${left.join("; ")}`}</MiniText>
          </Line>
        </MiniStack>
      ) : (
        <Line>
          <MiniText variant="strong">
            {t("folders.matrix.standard_progress", {
              planned: countOf(short),
              tasks: tasks(countOf(full)),
              points: pointsOf(short, pts),
              maxPoints: t("difficulty.points", { count: pointsOf(full, pts) }),
            })}
          </MiniText>
        </Line>
      )}
      <MiniRow>{DIFFICULTY_LEVELS.map(levelBadge)}</MiniRow>
    </MiniCard>
  ) : (
    <MiniCard tone="slate" mark={{ pointer: false }}>
      <Line>
        <MiniText variant="strong">{t("folders.matrix.plan_line", { plan: plan(short) })}</MiniText>
      </Line>
      <MiniRow>{DIFFICULTY_LEVELS.map(levelBadge)}</MiniRow>
    </MiniCard>
  );
  const planStep = (
    <HelpStep
      key="plan"
      title={standard ? t("help.matrix.steps.plan.title") : t("help.matrix.steps.plan.title_free")}
      illustration={
        <MiniStack>
          <MiniRow>
            <GradeTab selected check={standard ? "off" : null}>
              {standard
                ? t("folders.matrix.tab_target", { grade, planned: countOf(short), total: countOf(full) })
                : `${grade} · ${tasks(countOf(short))}`}
            </GradeTab>
          </MiniRow>
          {planBar}
        </MiniStack>
      }
    >
      <p>
        {standard
          ? t("help.matrix.steps.plan.text", {
              tasks: tasks(countOf(full)),
              a: full.A,
              b: full.B,
              c: full.C,
              points: t("difficulty.points", { count: pointsOf(full, pts) }),
            })
          : t("help.matrix.steps.plan.text_free")}
      </p>
      {pointsNote}
    </HelpStep>
  );

  // Saving (1), and the amber panel of a save someone else's save came before (2): the panel then holds the choice,
  // the toolbar's Cancel and Save wait meanwhile (two moments, a line between them).
  const saveStep = (
    <HelpStep
      key="save"
      title={t("help.matrix.steps.save.title")}
      illustration={
        <MiniStack>
          {wide && (
            <MiniRow className="justify-between gap-y-3">
              <MiniText variant="strong">{t("folders.matrix.edit_title")}</MiniText>
              <MiniRow className="gap-y-3">
                <MiniText variant="muted">{t("folders.matrix.unsaved")}</MiniText>
                <MiniButton variant="quiet" icon={XMarkIcon}>
                  {label.cancel}
                </MiniButton>
                <MiniButton variant="primary" mark="1">
                  {label.save}
                </MiniButton>
                {copyable && more}
              </MiniRow>
            </MiniRow>
          )}
          <MiniCard tone="red">{t("folders.matrix.fix_errors")}</MiniCard>
          {!wide && (
            <MiniRow className="flex-nowrap">
              <MiniButton variant="outline">{label.cancel}</MiniButton>
              <MiniButton variant="primary" mark="1">
                {label.save}
              </MiniButton>
            </MiniRow>
          )}
          <MiniDivider />
          <MiniCard tone="amber" mark={{ label: "2", pointer: false }}>
            <Line>
              <MiniText variant="strong">{t("folders.matrix.conflict_title")}</MiniText>
            </Line>
            <Line>
              <MiniText>{t("folders.matrix.conflict_text")}</MiniText>
            </Line>
            <Line>
              <MiniText>{`• ${t("folders.matrix.conflict_added", { name: `${ex("topic_2")} (${grade}, ${quarterLabel(t, 2)}, ${week(3)})` })}`}</MiniText>
            </Line>
            <MiniRow className="gap-y-3">
              <MiniButton variant="primary">{label.keep}</MiniButton>
              <MiniButton variant="quiet">{label.discard}</MiniButton>
            </MiniRow>
          </MiniCard>
        </MiniStack>
      }
    >
      <p>
        <HelpText i18nKey="help.matrix.steps.save.text" values={{ save: label.save, cancel: label.cancel }} />
        {!wide && ` ${t("help.matrix.steps.save.text_phone")}`}
      </p>
      <p>{t("help.matrix.steps.save.text_errors")}</p>
      <p>
        <HelpText i18nKey="help.matrix.steps.save.text_conflict" values={{ keep: label.keep, discard: label.discard }} />
      </p>
    </HelpStep>
  );

  return (
    <>
      <HelpIntro>{t("help.matrix.intro")}</HelpIntro>

      <HelpFlow pipeline="bts" current="matrix" />

      {/* A reader of an empty matrix sees none of the parts below yet: they come once it is planned. */}
      {!canEdit && empty && <HelpNote>{t("help.matrix.notes.empty_reader", { state: t("folders.matrix.empty_text_view") })}</HelpNote>}

      {canEdit ? (
        // The empty page's ways in, in its order: a ready matrix to copy first where the page recommends it.
        <HelpSteps>
          {copyFirst && copyStep}
          {empty ? startStep : gradeStep}
          {empty && copyable && !copyFirst && copyStep}
          {!empty && editStep}
          {templateStep}
          {addStep}
          {countsStep}
          {planStep}
          {saveStep}
          {/* Tasks are given for saved rows, in the reading view: none yet on the empty page. */}
          {!empty && tasksStep}
        </HelpSteps>
      ) : (
        <HelpSteps>
          {gradeStep}
          {summaryStep}
          {rowsStep}
          {bankStep}
          {tasksStep}
        </HelpSteps>
      )}

      <HelpRoles
        rows={[
          { key: "admin", role: t("folders.roles.admin"), Icon: KeyIcon, can: t("help.matrix.roles.admin") },
          { key: "moderator", role: t("folders.roles.moderator"), Icon: ShieldCheckIcon, can: t("help.matrix.roles.moderator") },
          { key: "expert", role: t("folders.roles.expert"), Icon: CheckBadgeIcon, can: t("help.matrix.roles.expert") },
          { key: "author", role: t("folders.roles.author"), Icon: PencilIcon, can: t("help.matrix.roles.author") },
          { key: "viewer", role: t("folders.roles.viewer"), Icon: EyeIcon, can: t("help.matrix.roles.viewer") },
        ]}
      />

      <HelpTips
        items={
          canEdit
            ? [
                <HelpText key="save" i18nKey="help.matrix.tips.save_all" values={{ save: label.save }} />,
                <HelpText key="quarters" i18nKey="help.matrix.tips.quarters" values={roleLabels} />,
                // A matrix with rows is replaced by a copy from the header's "⋯" (the empty page has its copy step).
                copyable && !empty && <HelpText key="copy" i18nKey="help.matrix.tips.copy" values={{ copy: label.copy }} />,
                !hasTemplate && t("help.matrix.tips.template_elsewhere"),
              ]
            : [<HelpText key="none" i18nKey="help.matrix.tips.no_topic" values={{ none: t("folders.matrix.no_topic") }} />, t("help.matrix.tips.per_grade")]
        }
      />
    </>
  );
}
